/**
 * StateAggregatorContext - Generic Multi-Source State Management System
 *
 * This module provides a React context-based state aggregation system that allows multiple
 * components to contribute values to the same state keys without conflicts. Instead of
 * components overwriting each other's values, they all contribute to an aggregated state
 * that can be consumed in different ways (last value, all values, etc.).
 *
 * Key Features:
 * - Multi-source state management with automatic conflict resolution
 * - Automatic cleanup when components unmount using handle-based tracking
 * - Flexible value retrieval (last value, all values, deduplicated values)
 * - Type-safe generic implementation for any state shape
 *
 * Usage Pattern:
 * 1. Call `createStateAggregatorContext<T>()` to create a typed context for your state shape
 * 2. Use the returned `useAddStateValue` hook to contribute values from multiple sources
 * 3. Use the returned hooks to consume aggregated values in various ways
 *
 * Real-world Applications in this codebase:
 * - Command Bar: Multiple providers contribute command bar configurations
 * - AI Extraction Context: Multiple boundary types contribute extraction configurations
 * - Template Aggregation: Multiple components contribute templates that are flattened
 * - Dynamic Configuration Management: UI components can switch between aggregated configs
 *
 * @example
 * ```typescript
 * interface MyState {
 *   configs: Config[];
 *   templates: Template[];
 * }
 *
 * const { context, useAddStateValue, useStateAggregatorAllValues } =
 *   createStateAggregatorContext<MyState>();
 *
 * // In component A:
 * function ComponentA() {
 *   useAddStateValue('configs', configA);
 *   return <div>A</div>;
 * }
 *
 * // In component B:
 * function ComponentB() {
 *   useAddStateValue('configs', configB);
 *   return <div>B</div>;
 * }
 *
 * // In consumer component:
 * const allConfigs = useStateAggregatorAllValues('configs'); // [configA, configB]
 * ```
 */

import { createContext, use, useEffect, useMemo, useState } from 'react';

/**
 * Internal storage entry type that pairs a component handle with its contributed value.
 * The handle (object reference) is used for automatic cleanup when components unmount.
 */
type IStateAggregatorStoreEntry<V> = [object, V];

/**
 * Internal storage structure that maps state keys to arrays of [handle, value] pairs.
 * Each key can have multiple values from different component sources.
 */
export type IStateAggregatorStore<T extends object> = {
	[K in keyof T]?: IStateAggregatorStoreEntry<T[K]>[];
};

/**
 * Proxy interface that provides convenient access methods for aggregated state values.
 * Each state key gets `.last()` and `.all()` methods for flexible value retrieval.
 */
export type IStateAggregatorStoreProxy<T extends object> = {
	[K in keyof T]: {
		/** Get the most recently added value for this key */
		last: () => T[K] | undefined;
		/** Get all values for this key, optionally deduplicated */
		all: (deduplicate?: boolean) => Array<T[K]>;
	};
};

/**
 * Core interface for state aggregator functionality.
 * Provides methods to add/remove values and access the current state.
 */
export type IStateAggregatorValue<T extends object> = {
	/** Add a value for a specific key, associated with a component handle */
	addStateValue: <K extends keyof T>(key: K, value: T[K], handle: object) => void;
	/** Remove a value for a specific key, identified by component handle */
	removeStateValue: <K extends keyof T>(key: K, handle: object) => void;
	/** Get the most recently added value for a key */
	getLastValue: <K extends keyof T>(key: K) => T[K] | undefined;
	/** The current aggregated state store */
	state: IStateAggregatorStore<T>;
};

/**
 * Hook that creates a state aggregator instance for managing multi-source state.
 *
 * This hook provides the core functionality for aggregating state values from multiple
 * components. Each component can contribute values using unique handles, and the hook
 * manages automatic cleanup when components unmount.
 *
 * @template T - The shape of the state object to aggregate
 * @returns An object with methods to add/remove values and access the current state
 * @example
 * ```typescript
 * interface MyState {
 *   configs: Config[];
 *   enabled: boolean;
 * }
 *
 * function MyProvider({ children }) {
 *   const aggregator = useCreateStateAggregator<MyState>();
 *   return (
 *     <MyContext.Provider value={aggregator}>
 *       {children}
 *     </MyContext.Provider>
 *   );
 * }
 * ```
 */
export function useCreateStateAggregator<T extends object>(): IStateAggregatorValue<T> {
	const [state, setState] = useState<IStateAggregatorStore<T>>({});

	/**
	 * Adds or updates a value for a specific state key.
	 * If the handle already exists for this key, its value is replaced.
	 * Otherwise, a new [handle, value] pair is added.
	 */
	const addStateValue: IStateAggregatorValue<T>['addStateValue'] = useMemo(
		() => (key, value, handle) => {
			setState((s) => {
				const currentValue: IStateAggregatorStoreEntry<T[typeof key]>[] = s[key] ?? [];
				const existing = currentValue.find(([h]) => h === handle);
				if (existing && Object.is(existing[1], value)) {
					return s;
				}
				const newState = { ...s };
				newState[key] = [...currentValue.filter(([h]) => h !== handle), [handle, value]];
				return newState;
			});
		},
		[],
	);

	/**
	 * Removes a value for a specific state key, identified by its handle.
	 * If this was the last value for the key, the key is removed entirely.
	 */
	const removeStateValue: IStateAggregatorValue<T>['removeStateValue'] = useMemo(
		() => (key, handle) => {
			setState((s) => {
				const currentValue: IStateAggregatorStoreEntry<T[typeof key]>[] = s[key] ?? [];
				if (!currentValue.some(([h]) => h === handle)) {
					return s;
				}
				const newState = { ...s };
				newState[key] = currentValue.filter(([h]) => h !== handle);
				if (newState[key].length === 0) {
					// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
					delete newState[key];
				}
				return newState;
			});
		},
		[],
	);

	/**
	 * Gets the most recently added value for a specific state key.
	 * Returns undefined if no values exist for the key.
	 */
	const getLastValue: IStateAggregatorValue<T>['getLastValue'] = useMemo(() => {
		return (key) =>
			typeof state[key] === 'object' && typeof state[key][state[key].length - 1] === 'object' ?
				state[key][state[key].length - 1]?.[1]
			:	undefined;
	}, [state]);

	return {
		state,
		addStateValue,
		removeStateValue,
		getLastValue,
	};
}

/**
 * Creates a complete state aggregator context system with React context and helper functions.
 *
 * This factory function creates a typed React context along with components and hooks
 * for managing aggregated state. It returns everything needed to implement the state
 * aggregator pattern in a React component tree.
 *
 * @template T - The shape of the state object to aggregate
 * @returns An object containing the context, components, and hooks for state aggregation
 * @example
 * ```typescript
 * interface MyState {
 *   configs: Config[];
 *   templates: Template[];
 * }
 *
 * const {
 *   context: MyContext,
 *   AddStateValue: AddMyValue,
 *   useStateAggregatorAllValues: useMyAllValues,
 *   useStateAggregatorLastValue: useMyLastValue,
 *   useStateAggregatorProxy: useMyProxy,
 * } = createStateAggregatorContext<MyState>();
 *
 * // Usage in components:
 * <AddMyValue okey="configs" value={myConfig} />
 * const allConfigs = useMyAllValues('configs');
 * ```
 */
export function createStateAggregatorContext<T extends object>() {
	const context = createContext<IStateAggregatorValue<T>>({
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		addStateValue: () => {},
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		removeStateValue: () => {},
		getLastValue: () => {
			return undefined;
		},
		state: {},
	});

	/**
	 * Hook that contributes a value to the aggregated state.
	 *
	 * This hook automatically adds its value when the component mounts and removes it when unmounted.
	 * If the value changes, the old value is replaced with the new one. Each hook call gets
	 * a unique handle for tracking and cleanup.
	 *
	 * @param okey - The state key to contribute to
	 * @param value - The value to contribute
	 * @example
	 * ```typescript
	 * // Multiple components can contribute to the same key:
	 * function ComponentA() {
	 *   useAddStateValue('configs', configA);
	 *   return <div>A</div>;
	 * }
	 *
	 * function ComponentB() {
	 *   useAddStateValue('configs', configB);
	 *   return <div>B</div>;
	 * }
	 * // Result: configs will contain [configA, configB]
	 * ```
	 */
	function useAddStateValue<K extends keyof T>(okey: K, value: T[K]) {
		const { addStateValue, removeStateValue } = use(context);
		const [handle] = useState({});
		useEffect(() => {
			addStateValue(okey, value, handle);
			return () => {
				removeStateValue(okey, handle);
			};
		}, [addStateValue, handle, okey, removeStateValue, value]);
	}

	/**
	 * Component that contributes a value to the aggregated state.
	 *
	 * This component automatically adds its value when mounted and removes it when unmounted.
	 * If the value changes, the old value is replaced with the new one. Each instance gets
	 * a unique handle for tracking and cleanup.
	 *
	 * @param arg -
	 * @param arg.okey - The state key to contribute to
	 * @param arg.value - The value to contribute
	 * @returns null (this is a side-effect only component)
	 * @example
	 * ```typescript
	 * // Multiple components can contribute to the same key:
	 * <AddStateValue okey="configs" value={configA} />
	 * <AddStateValue okey="configs" value={configB} />
	 * // Result: configs will contain [configA, configB]
	 * ```
	 */
	function AddStateValue<K extends keyof T>({ okey, value }: { okey: K; value: T[K] }) {
		useAddStateValue(okey, value);
		return null;
	}

	/**
	 * Hook that returns the most recently added value for a specific state key.
	 *
	 * This is useful when you want the "winning" value from multiple contributors,
	 * using a last-wins strategy.
	 *
	 * @param key - The state key to get the last value for
	 * @returns The most recently added value, or undefined if no values exist
	 * @example
	 * ```typescript
	 * // If multiple components contribute configs:
	 * <AddStateValue okey="config" value={configA} />
	 * <AddStateValue okey="config" value={configB} />
	 *
	 * // This will return configB (the last one added):
	 * const currentConfig = useStateAggregatorLastValue('config');
	 * ```
	 */
	function useStateAggregatorLastValue<K extends keyof T>(key: K): T[K] | undefined {
		const values = use(context).state[key];
		if (!values || values.length === 0) {
			return undefined;
		}

		return values[values.length - 1]?.[1];
	}

	/**
	 * Hook that returns all values for a specific state key from all contributors.
	 *
	 * This is useful when you need to aggregate or process all contributed values.
	 * For example, collecting all templates from multiple sources or all available
	 * configurations for a dropdown selection.
	 *
	 * @param key - The state key to get all values for
	 * @param deduplicate - Whether to remove duplicate values (default: true)
	 * @returns Array of all contributed values for the key
	 * @example
	 * ```typescript
	 * // Multiple components contribute templates:
	 * <AddStateValue okey="templates" value={[templateA]} />
	 * <AddStateValue okey="templates" value={[templateB]} />
	 *
	 * // Get all templates and flatten them:
	 * const allTemplates = useStateAggregatorAllValues('templates').flat();
	 * ```
	 */
	function useStateAggregatorAllValues<K extends keyof T>(key: K, deduplicate = true): Array<T[K]> {
		const valuesAndHandles = use(context).state[key];
		if (!valuesAndHandles || valuesAndHandles.length === 0) {
			return [];
		}

		const values = valuesAndHandles.map((v) => v[1]);
		if (deduplicate) {
			return Array.from(new Set(values));
		}

		return values;
	}

	/**
	 * Hook that returns a proxy object providing convenient access to aggregated state.
	 *
	 * The proxy allows you to access state keys directly and call `.last()` or `.all()`
	 * methods on them. This provides a more fluent API compared to calling the individual
	 * hook functions.
	 *
	 * @returns A proxy object where each state key has `.last()` and `.all()` methods
	 * @example
	 * ```typescript
	 * const proxy = useStateAggregatorProxy();
	 *
	 * // These are equivalent:
	 * const lastConfig = proxy.configs.last();
	 * const lastConfig2 = useStateAggregatorLastValue('configs');
	 *
	 * // These are equivalent:
	 * const allConfigs = proxy.configs.all();
	 * const allConfigs2 = useStateAggregatorAllValues('configs');
	 * ```
	 */
	function useStateAggregatorProxy(): IStateAggregatorStoreProxy<T> {
		const ctx = use(context);

		return new Proxy(ctx.state, {
			get(target: IStateAggregatorStore<T>, key: string | symbol, _receiver: unknown) {
				if (typeof key !== 'string') {
					return Reflect.get(target, key);
				}

				const typedKey = key as keyof IStateAggregatorStore<T>;
				return {
					all: (deduplicate = false) => {
						const valuesAndHandles = target[typedKey];
						// eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
						if (!valuesAndHandles || valuesAndHandles.length === 0) {
							return [];
						}

						const values = valuesAndHandles.map((v) => v[1]);
						if (deduplicate) {
							return Array.from(new Set(values));
						}

						return values;
					},
					last: () => {
						const values = target[typedKey];
						// eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
						if (!values || values.length === 0) {
							return undefined;
						}

						return values.at(-1)?.[1];
					},
				};
			},
		}) as unknown as IStateAggregatorStoreProxy<T>;
	}

	return {
		context,
		AddStateValue,
		useAddStateValue,
		useStateAggregatorLastValue,
		useStateAggregatorAllValues,
		useStateAggregatorProxy,
	};
}
